import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack, styled } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  calculateCredits,
  getCommunicationServiceAccounts,
  getWorkAssignmentByCurrentRecruiterAndJob,
  IBulkSmsDTO,
  ICandidate,
  ICommunicationServiceAccount,
  IJob,
  IJobCandidateInteractionDTO,
  ITeam,
  processPhoneNumbers,
  sendBulkSms,
} from 'wisestep-api';
import {
  Item,
  useWsEditor,
  WsButton,
  WsColors,
  WsEditor,
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';
import { object, string } from 'yup';

import { useUserContext } from '../../context';
import { failed, success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';

import { useWorkspaceCredit } from './helper/apiCalls';

export interface IBulkSmsProps {
  selectedCandidates: ICandidate[];
  job: IJob;
  handleClose: () => void;
  jobTitle: string;
}

const DrawerFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 1,
  justifyContent: 'revert',
  background: WsColors.TertiaryShades100,
  width: '50vw',
}));

const schema = object().shape({
  message: string().required(),
  from: string().required(),
});

const BulkSms = ({ selectedCandidates, job, handleClose, jobTitle }: IBulkSmsProps) => {
  const [fromPhoneNumbers, setFromPhoneNumbers] = useState<WsSelectObj[]>([]);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [processedCandidates, setProcessedCandidates] = useState<IJobCandidateInteractionDTO[]>([]);
  const [processedAndValidCandidates, setProcessedAndValidCandidates] = useState<
    IJobCandidateInteractionDTO[]
  >([]);
  const [workAssignmentTeam, setWorkAssignmentTeam] = useState<ITeam>();
  const availableCredit = useWorkspaceCredit();

  const [messageStat, setMessageStat] = useState({
    shortestSms: 0,
    longestSms: 0,
    totalCreditsNeeded: 0,
  });

  const setNotification = useNotificationStore((state) => state.setNotification);

  const user = useUserContext();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      message: `Hi {{CANDIDATE_NAME}}, wanted to check if you would be interested in the role of ${
        job.title ?? jobTitle
      } ?`,
      from: '',
    },
  });

  const { setValue, watch } = methods;

  const message = watch('message');

  const editor = useWsEditor(
    `Hi {{CANDIDATE_NAME}}, wanted to check if you would be interested in the role of ${
      job.title ?? jobTitle
    } ?`,
    () => {},
    (text) => {
      methods.setValue('message', text ?? '');
    }
  );

  useEffect(() => {
    const [shortestSms, longestSms, totalCreditsNeeded] = calculateCredits(
      processedCandidates,
      message
    );
    setMessageStat({
      shortestSms: shortestSms,
      longestSms: longestSms,
      totalCreditsNeeded: totalCreditsNeeded,
    });
  }, [message, processedCandidates]);

  const fetchFromPhoneNumbers = useCallback(async () => {
    const localStorageResponse: ICommunicationServiceAccount[] = JSON.parse(
      localStorage.getItem('communicationAccounts') ?? '[]'
    );
    let communicationAccounts: Array<ICommunicationServiceAccount> = [];
    if (localStorageResponse.length > 0) {
      communicationAccounts = localStorageResponse;
    } else {
      const response = await getCommunicationServiceAccounts();
      localStorage.setItem('communicationAccounts', JSON.stringify(response.data));
      communicationAccounts = response.data;
    }
    const fromPhoneList = constructSelectObj(communicationAccounts);
    setFromPhoneNumbers(fromPhoneList);
    setValue('from', fromPhoneList[0].value.toString());
  }, [setValue]);

  useEffect(() => {
    const getWorkAssignmentTeam = async () => {
      const response = await getWorkAssignmentByCurrentRecruiterAndJob(job?.id ?? '');
      console.log(response.data?.team?.id);
      setWorkAssignmentTeam(response.data?.team);
    };
    getWorkAssignmentTeam();
  }, [job?.id]);

  const constructSelectObj = (accounts: Array<ICommunicationServiceAccount>) => {
    const fromPhoneNumbers: Array<WsSelectObj> = [];
    accounts.forEach((account) => {
      account.phoneNumbers.forEach((phoneNumber) => {
        if (
          account.serviceName != 'ZOOM' &&
          phoneNumber.canSendSms &&
          phoneNumber.sharedWith?.includes(user.userDTO?.email ?? '')
        ) {
          fromPhoneNumbers.push({
            label: phoneNumber.phoneNumber,
            value: JSON.stringify({
              selectedAccount: account.id,
              from: phoneNumber.phoneNumber,
              serviceAccountId: account.serviceAccountId,
              serviceName: account.serviceName,
            }),
          });
        }
      });
    });
    return fromPhoneNumbers;
  };

  const getContactAction = useCallback(
    (data) => {
      const selectedPhone = JSON.parse(data.from);
      const contactAction = {
        result: {
          result: [],
          notes: '',
          createdOn: new Date(),
          createdBy: '',
          createdByEmail: '',
        },
        notes: '',
        actions: [''],
        createdOn: new Date(),
        createdBy: user.userDTO?.email ?? '',
        createdByEmail: '',
        actionId: '',
        communicationId: '',
        communicationAccountId: selectedPhone.serviceAccountId,
        phoneNumber: '',
      };
      contactAction.notes = data.message;
      contactAction.actions = ['Message', 'Sent SMS/Text'];
      return contactAction;
    },
    [user]
  );

  const sendSms = useCallback(
    async (data) => {
      setIsSendingSms(true);
      if (availableCredit < messageStat.totalCreditsNeeded) {
        setNotification(failed('You do not have enough credits.'));
        setIsSendingSms(false);
      } else {
        processedCandidates.map((processedCandidate) => {
          processedCandidate.mostRecentConversation = getContactAction(data);
          processedCandidate.mostRecentConversation.phoneNumber =
            processedCandidate.phoneNumber ?? '';
          processedCandidate.mostRecentConversation.notes =
            data.message.replace('{{CANDIDATE_NAME}}', processedCandidate.name) +
            '\n' +
            'Reply Yes/No or STOP to opt-out';
        });
        const fromPhoneNumber = JSON.parse(data.from);
        const payload: IBulkSmsDTO = {
          candidates: processedAndValidCandidates,
          from: fromPhoneNumber.from,
          message: data.message,
          notes: '',
          selectedAccount: fromPhoneNumber.selectedAccount,
          serviceName: fromPhoneNumber.serviceName,
        };
        try {
          const invalidCandidateCount = processedCandidates.filter(
            (processedCandidate) => !processedCandidate.isValid
          ).length;
          if (processedAndValidCandidates.length >= 1) {
            const stats = await sendBulkSms(payload);

            const sentSmsCount = stats.data.sentSmsCount;
            const skippedSmsCount = stats.data.skippedSmsCount;
            processedCandidates.length > 1
              ? sentSmsCount == 0
                ? setNotification(
                    warning(
                      `Candidate selected : ${processedCandidates.length} \n Message sent : ${sentSmsCount} \n Candidates skipped to prevent spam : ${skippedSmsCount} \n Invalid candidate contact : ${invalidCandidateCount}`,
                      15000
                    )
                  )
                : setNotification(
                    success(
                      `Candidate selected : ${processedCandidates.length} \n Message sent successfully : ${sentSmsCount} \n Candidates skipped to prevent spam : ${skippedSmsCount} \n Invalid candidate contact : ${invalidCandidateCount}`,
                      15000
                    )
                  )
              : skippedSmsCount == 0 && invalidCandidateCount == 0
              ? setNotification(success(`Message sent successfully`, 15000))
              : skippedSmsCount > 0
              ? setNotification(failed(`Message could not sent due to spam`, 15000))
              : setNotification(failed(`Something went wrong `, 15000));
            setIsSendingSms(false);
            handleClose();
          } else {
            processedCandidates.length == 1
              ? setNotification(failed(`Message could not sent due to invalid candidate `, 15000))
              : setNotification(
                  warning(`All ${invalidCandidateCount} candidates were invalid`, 15000)
                );
          }
        } catch (err: any) {
          setNotification(failed(err.message));
          setIsSendingSms(false);
        }
      }
    },
    [
      availableCredit,
      messageStat.totalCreditsNeeded,
      setNotification,
      processedCandidates,
      getContactAction,
      handleClose,
    ]
  );

  useEffect(() => {
    fetchFromPhoneNumbers();
    const processedCandidatesTemp = processPhoneNumbers(
      selectedCandidates,
      job,
      {
        email: user.userDTO?.email ?? '',
      },
      workAssignmentTeam
    );
    const validCandidate = processedCandidatesTemp.filter(
      (processedCandidate) => processedCandidate.isValid
    );
    setProcessedAndValidCandidates(validCandidate);
    setProcessedCandidates(processedCandidatesTemp);
  }, [fetchFromPhoneNumbers, job, selectedCandidates, user.userDTO?.email]);
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(sendSms)}>
        <div
          style={{
            margin: 12,
            border: `1.5px solid ${WsColors.BorderGrey}`,
            borderRadius: 4,
            minHeight: 150,
          }}
        >
          <WsEditor editor={editor} toolbar={false} />
        </div>
        <Stack direction={'row'} justifyContent={'space-between'} p={2}>
          <Stack flex={1}>
            <WsTypography
              bold={true}
            >{`Shortest SMS Length: ${messageStat.shortestSms}`}</WsTypography>
            <WsTypography
              bold={true}
            >{`Longest SMS Length: ${messageStat.longestSms}`}</WsTypography>
            <WsTypography
              bold={true}
            >{`Total Credits Needed: ${messageStat.totalCreditsNeeded}`}</WsTypography>
          </Stack>
          <Stack flex={1}>
            <WsSelect
              name="from"
              selectArray={fromPhoneNumbers}
              defaultValue={fromPhoneNumbers[0] ? fromPhoneNumbers[0].label : ''}
            />
          </Stack>
        </Stack>
        {processedCandidates.map((candidate, i) => (
          <Item key={i} sx={{ boxShadow: `2px 2px 2px ${WsColors.BorderGrey}` }}>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <WsTypography>{candidate.name ?? ''}</WsTypography>
              <WsTypography color={candidate.isValid ? WsTypoColors.BLACK : WsTypoColors.ERROR}>
                {candidate.phoneNumber ?? ''}
              </WsTypography>
            </Stack>
          </Item>
        ))}
        <DrawerFooter>
          <Stack p={1} spacing={2} direction="row" justifyContent={'flex-end'}>
            <WsButton variant="outlined" size="large" onClick={handleClose}>
              Cancel
            </WsButton>
            <WsButton variant="contained" size="large" type="submit" disabled={isSendingSms}>
              Send
            </WsButton>
          </Stack>
        </DrawerFooter>
      </form>
    </FormProvider>
  );
};

export default BulkSms;
